import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

export function CookiePolicy() {
  const { t, i18n } = useTranslation('legal')

  const technicalItems = t('cookies.types.technical.items', { returnObjects: true }) as string[]
  const thirdPartyItems = t('cookies.types.thirdParty.items', { returnObjects: true }) as string[]
  const managementItems = t('cookies.management.items', { returnObjects: true }) as string[]

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('cookies.meta.title')}</title>
        <meta name="description" content={t('cookies.meta.description')} />
      </Helmet>

      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-gray-900">
        <div className="container-custom">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white"
          >
            {t('cookies.title')}
          </motion.h1>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto prose prose-sm sm:prose-lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('cookies.what.title')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                {t('cookies.what.text')}
              </p>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 mt-6 sm:mt-8">
                {t('cookies.types.title')}
              </h2>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 mt-4 sm:mt-6">
                {t('cookies.types.technical.title')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                {t('cookies.types.technical.intro')}
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 space-y-1.5 sm:space-y-2">
                {technicalItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 mt-4 sm:mt-6">
                {t('cookies.types.analytics.title')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                {t('cookies.types.analytics.text')}
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 mt-4 sm:mt-6">
                {t('cookies.types.thirdParty.title')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                {t('cookies.types.thirdParty.intro')}
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 space-y-1.5 sm:space-y-2">
                {thirdPartyItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 mt-6 sm:mt-8">
                {t('cookies.management.title')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                {t('cookies.management.intro')}
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 space-y-1.5 sm:space-y-2">
                {managementItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                {t('cookies.management.warning')}
              </p>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 mt-6 sm:mt-8">
                {t('cookies.browser.title')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                {t('cookies.browser.intro')}
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 space-y-1.5 sm:space-y-2">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">
                    {t('cookies.browser.chrome')}
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">
                    {t('cookies.browser.firefox')}
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">
                    {t('cookies.browser.safari')}
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">
                    {t('cookies.browser.edge')}
                  </a>
                </li>
              </ul>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 mt-6 sm:mt-8">
                {t('cookies.contact.title')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700">
                {t('cookies.contact.text')}
              </p>

              <p className="text-gray-500 mt-6 sm:mt-8 text-xs sm:text-sm">
                {t('cookies.lastUpdate')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
